import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { AxiosError } from 'axios';
import { Button, Table, Divider, Icon, Modal, Form, Switch } from 'antd';

import { CustomInput as Input } from './components/CustomAntd';
import { PostPutProductsReq } from './types/apiRequest/products.request';
import { GetProductsRes, Product } from './types/apiResponse/products.response';
import httpUtils from './utils/httpUtils';
import { ApplicationState } from './reducers';

type E = React.ChangeEvent<HTMLInputElement>;

const mapStateToProps = (state: ApplicationState) => ({
  officeId: state.baseReducer.selectedOfficeId,
});

const mapDispatchToProps = () => ({});

type Props = RouteComponentProps<{}> &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

interface State {
  products: Product[];
  visibleNew: boolean;
  visibleEdit: boolean;
  productId: number;
  productName: string;
  unitPrice?: number;
  quantityUnit: string;
  visibleFlag: number;
}

const initialState = {
  products: [],
  visibleNew: false,
  visibleEdit: false,
  productId: 0,
  productName: '',
  unitPrice: undefined,
  quantityUnit: '',
  visibleFlag: 1,
};

class AdminProduct extends React.PureComponent<Props, State> {
  columns = [
    {
      title: '製品名',
      dataIndex: 'productName',
      key: 'productName',
      defaultSortOrder: 'ascend',
      sorter: (a: Product, b: Product) =>
        a.productName < b.productName ? -1 : 1,
    },
    {
      title: '単価(円)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      sorter: (a: Product, b: Product) => (a.unitPrice < b.unitPrice ? -1 : 1),
    },
    {
      title: '数量単位',
      dataIndex: 'quantityUnit',
      key: 'quantityUnit',
      sorter: (a: Product, b: Product) =>
        a.quantityUnit < b.quantityUnit ? -1 : 1,
    },
    {
      title: '表示 / 非表示',
      render: (record: Product) => {
        return (
          <Switch
            checked={!!record.visibleFlag}
            onChange={() => {
              this.handleSwitch(record);
            }}
          />
        );
      },
    },
    {
      title: '編集 / 削除',
      dataIndex: '',
      key: 'x',
      // @ts-ignore
      render: (record: Product) => (
        <span>
          <Icon
            type="edit"
            onClick={() => {
              this.setState({
                ...record,
                visibleEdit: true,
              });
            }}
          />
          <Divider type="vertical" />
          <Icon
            type="delete"
            onClick={() => {
              const handleDelete = this.handleDelete;
              Modal.confirm({
                title: 'このデータを削除しますか？',
                content: '削除すると、元に戻せません。',
                cancelText: 'キャンセル',
                onOk() {
                  handleDelete(record.productId);
                },
                onCancel() {},
              });
            }}
          />
        </span>
      ),
    },
  ];

  state: State = initialState;

  handleFetch = async () => {
    const { officeId } = this.props;
    try {
      const res = await httpUtils.get(`/products`, {
        params: { officeId },
      });
      const products: GetProductsRes = res.data;
      this.setState({ products });
    } catch (error) {
      // Todo : 良い方法を考える
      const err: AxiosError = error;
      if (err.response) {
        const { status, statusText } = err.response;
        console.log(`Error! HTTP Status: ${status} ${statusText}`);
      }
    }
  };

  handlePost = async () => {
    const { officeId } = this.props;
    const { products, productName, unitPrice, quantityUnit } = this.state;
    const req: PostPutProductsReq = {
      officeId,
      productName,
      quantityUnit,
      unitPrice: unitPrice || 0,
      // clientId: 1
    };
    try {
      await httpUtils.post(`/products`, req);
      this.setState({ ...initialState, products });
      this.handleFetch();
    } catch (error) {
      const err: AxiosError = error;
      if (err.response) {
        const { status, statusText } = err.response;
        console.log(`Error! HTTP Status: ${status} ${statusText}`);
      }
    }
  };

  handlePut = async () => {
    const { officeId } = this.props;
    const {
      products,
      productId,
      productName,
      unitPrice,
      quantityUnit,
    } = this.state;
    const req: PostPutProductsReq = {
      officeId,
      productName,
      quantityUnit,
      unitPrice: unitPrice || 0,
      // clientId: 1
    };
    try {
      await httpUtils.put(`/products/${productId}`, req);
      this.setState({ ...initialState, products });
      this.handleFetch();
    } catch (error) {
      const err: AxiosError = error;
      if (err.response) {
        const { status, statusText } = err.response;
        console.log(`Error! HTTP Status: ${status} ${statusText}`);
      }
    }
  };

  handleDelete = async (id: number) => {
    const { officeId } = this.props;
    try {
      await httpUtils.delete(`/products/${id}`, {
        data: {
          officeId,
        },
      });
      this.handleFetch();
    } catch (error) {
      const err: AxiosError = error;
      if (err.response) {
        const { status, statusText } = err.response;
        console.log(`Error! HTTP Status: ${status} ${statusText}`);
      }
    }
  };

  handleSwitch = async (record: Product) => {
    const { officeId } = this.props;
    const { productId } = record;
    try {
      await httpUtils.put(`/products/${productId}`, {
        ...record,
        officeId,
        visibleFlag: record.visibleFlag === 1 ? 0 : 1,
      });
      this.handleFetch();
    } catch (error) {
      const err: AxiosError = error;
      if (err.response) {
        const { status, statusText } = err.response;
        console.log(`Error! HTTP Status: ${status} ${statusText}`);
      }
    }
  };

  onChangeProductName = (e: E) => {
    this.setState({ productName: e.target.value });
  };

  onChangeUnitPrice = (e: E) => {
    const unitPrice = parseFloat(e.target.value);
    if (unitPrice >= 0) {
      this.setState({ unitPrice });
      return;
    }
    this.setState({ unitPrice: undefined });
  };

  onChangeQuantityUnit = (e: E) => {
    this.setState({ quantityUnit: e.target.value });
  };

  componentDidMount() {
    const { officeId } = this.props;
    // ログインに戻す
    if (!officeId) {
      this.props.history.push('/login');
      return;
    }
    this.handleFetch();
  }

  render() {
    const {
      products,
      productId,
      productName,
      unitPrice,
      quantityUnit,
      visibleNew,
      visibleEdit,
    } = this.state;

    const isNewDuplicated = !!products.filter(
      x => x.productName === productName
    ).length;

    const isEditDuplicated = !!products
      .filter(x => x.productId !== productId)
      .filter(x => x.productName === productName).length;

    return (
      <>
        <Button
          onClick={() => {
            this.setState({ visibleNew: true });
          }}
          type="primary"
          style={{ marginBottom: 16 }}
        >
          追加
        </Button>
        <Table
          // @ts-ignore
          columns={this.columns}
          dataSource={products}
          rowKey="productId"
        />
        <Modal
          title="製品を追加"
          visible={visibleNew}
          onOk={() => this.handlePost()}
          onCancel={() => this.setState({ ...initialState, products })}
          cancelText="キャンセル"
          okButtonProps={{
            disabled:
              !productName ||
              unitPrice === initialState.unitPrice ||
              !quantityUnit ||
              isNewDuplicated,
          }}
        >
          <Form>
            <Form.Item
              validateStatus={isNewDuplicated ? 'error' : undefined}
              help={
                isNewDuplicated
                  ? '既に登録されている内容では登録できません'
                  : undefined
              }
            >
              <Input
                placeholder="製品名"
                value={productName}
                onChange={this.onChangeProductName}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder="数量単位"
                value={quantityUnit}
                onChange={this.onChangeQuantityUnit}
              />
              <Input
                placeholder="単価(円)"
                value={unitPrice}
                type="number"
                step="0.1"
                onChange={this.onChangeUnitPrice}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="作業を編集"
          visible={visibleEdit}
          onOk={() => this.handlePut()}
          onCancel={() => this.setState({ ...initialState, products })}
          cancelText="キャンセル"
          okButtonProps={{
            disabled:
              !productName ||
              unitPrice === initialState.unitPrice ||
              !quantityUnit ||
              isEditDuplicated,
          }}
        >
          <Form>
            <Form.Item
              validateStatus={isEditDuplicated ? 'error' : undefined}
              help={
                isEditDuplicated
                  ? '既に登録されている内容では登録できません'
                  : undefined
              }
            >
              <Input
                placeholder="製品名"
                value={productName}
                onChange={this.onChangeProductName}
              />
            </Form.Item>
            <Form.Item>
              <Input
                placeholder="数量単位"
                value={quantityUnit}
                onChange={this.onChangeQuantityUnit}
              />
              <Input
                placeholder="単価(円)"
                value={unitPrice}
                type="number"
                step="0.1"
                onChange={this.onChangeUnitPrice}
              />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminProduct);
